import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './services/auth'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import SearchPage from './pages/SearchPage'
import LicenseDetailPage from './pages/LicenseDetailPage'
import OwnershipChangesPage from './pages/OwnershipChangesPage'
import ReferenceCopyPage from './pages/ReferenceCopyPage'
import ConstructionWorksheetPage from './pages/ConstructionWorksheetPage'
import UsersPage from './pages/UsersPage'
import ProfilePage from './pages/ProfilePage'
import ImportManagementPage from './pages/admin/ImportManagementPage'
import GeneralSettingsPage from './pages/admin/GeneralSettingsPage'
import BackupPage from './pages/admin/BackupPage'
import DoNotContactPage from './pages/admin/DoNotContactPage'
import CRMExportPage from './pages/admin/CRMExportPage'
import ClientsPage from './pages/crm/ClientsPage'
import ClientDetailPage from './pages/crm/ClientDetailPage'
import TasksPage from './pages/crm/TasksPage'
import LeadsPage from './pages/crm/LeadsPage'
import LeadDetailPage from './pages/crm/LeadDetailPage'
import ConstructionDeadlinesPage from './pages/crm/ConstructionDeadlinesPage'
import CoordinationPage from './pages/CoordinationPage'
import AboutPage from './pages/AboutPage'
import AdminEmailPage from './pages/admin/AdminEmailPage'
import AuditLogPage from './pages/admin/AuditLogPage'
import SecurityAlertsPage from './pages/admin/SecurityAlertsPage'
import MyBusinessCardsPage from './pages/profile/MyBusinessCardsPage'

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

// Admin-only route wrapper — redirects non-admins to home
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return user?.is_superuser ? <>{children}</> : <Navigate to="/" replace />
}

/**
 * Employee-or-admin, matching the API's `_require_employee_or_admin`.
 *
 * A viewer is read-only, so a page whose purpose is editing would only bounce them
 * off the API. Same shape as AdminRoute, and like it this is UX only — the API
 * enforces the same rule independently.
 */
function EmployeeRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const allowed = user?.is_superuser || (user?.roles ?? []).includes('employee')
  return allowed ? <>{children}</> : <Navigate to="/" replace />
}

// Public route (redirect to dashboard if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<Navigate to="/" replace />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="licenses/ownership-changes" element={<AdminRoute><OwnershipChangesPage /></AdminRoute>} />
            <Route path="licenses/:callSign" element={<LicenseDetailPage />} />
            <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
            <Route path="admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
            {/* The four e-mail pages became tabs on one Email page. The old paths
                redirect rather than 404 — they are in bookmarks, in the browser
                history and in older release notes. */}
            <Route path="admin/email" element={<AdminRoute><AdminEmailPage /></AdminRoute>} />
            <Route path="admin/smtp" element={<Navigate to="/admin/email?tab=delivery" replace />} />
            <Route path="admin/import" element={<AdminRoute><ImportManagementPage /></AdminRoute>} />
            <Route path="admin/settings" element={<AdminRoute><GeneralSettingsPage /></AdminRoute>} />
            <Route path="admin/notifications" element={<Navigate to="/admin/email?tab=reminders" replace />} />
            <Route path="admin/backup" element={<AdminRoute><BackupPage /></AdminRoute>} />
            <Route path="admin/do-not-contact" element={<EmployeeRoute><DoNotContactPage /></EmployeeRoute>} />
            <Route path="admin/crm-export" element={<AdminRoute><CRMExportPage /></AdminRoute>} />
            <Route path="admin/business-cards" element={<Navigate to="/admin/email?tab=signatures" replace />} />
            <Route path="admin/email-templates" element={<Navigate to="/admin/email?tab=templates" replace />} />
            <Route path="admin/audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
            <Route path="admin/security-alerts" element={<AdminRoute><SecurityAlertsPage /></AdminRoute>} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="profile/business-cards" element={<MyBusinessCardsPage />} />
            <Route path="crm/clients" element={<ClientsPage />} />
            <Route path="crm/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="crm/tasks" element={<TasksPage />} />
            <Route path="crm/leads" element={<LeadsPage />} />
            <Route path="crm/leads/:leadId" element={<LeadDetailPage />} />
            <Route path="construction-deadlines" element={<ConstructionDeadlinesPage />} />
            {/* In development. AdminRoute is UX; the API enforces require_admin itself. */}
            <Route path="coordination" element={<AdminRoute><CoordinationPage /></AdminRoute>} />
            <Route path="about" element={<AboutPage />} />
          </Route>

          {/* Standalone document route (no app chrome) for clean printing */}
          <Route
            path="/licenses/:callSign/reference"
            element={
              <ProtectedRoute>
                <ReferenceCopyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/licenses/:callSign/construction-worksheet"
            element={
              <ProtectedRoute>
                <ConstructionWorksheetPage />
              </ProtectedRoute>
            }
          />

          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  )
}

export default App
